/**
 * GlobalHeader Component
 * 
 * Renders the global header integrated with sidebar design:
 * - Same background color as sidebar (#203D5C)
 * - Header actions on the right (notifications, settings, user avatar)
 * 
 * Uses Zustand store for header state.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoundStore } from '@/stores';
import { useRouteMetadata } from '@/routing/useRouteMetadata';
import { Button } from '@/shared/ui/button';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "@/shared/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/shared/ui/dropdown-menu";
import { useLogout } from '@/features/auth/api/queries';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import { LogOut } from 'lucide-react';
import { ConfirmationDialog } from '@/shared/ui/confirmation-dialog';

export const GlobalHeader = () => {
    const { t } = useTranslation('common');
    const { showHeader } = useRouteMetadata();
    const user = useBoundStore.use.user();
    const { mutate: logout, isPending: isLoggingOut } = useLogout();
    const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

    // Hide header if showHeader is false
    if (showHeader === false) {
        return null;
    }

    const getUserInitials = (name: string) =>
        (name || "U")
            .split(" ")
            .map((word) => word.charAt(0))
            .join("")
            .toUpperCase()
            .slice(0, 2);

    const displayName = user
        ? [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || t('layout.header.user')
        : t('layout.header.user');
    const userInitials = getUserInitials(displayName);

    const handleLogoutClick = () => {
        setIsLogoutDialogOpen(true);
    };

    const handleConfirmLogout = () => {
        logout(undefined, {
            onSuccess: () => {
                setIsLogoutDialogOpen(false);
            },
        });
    };

    const handleCancelLogout = () => {
        if (!isLoggingOut) {
            setIsLogoutDialogOpen(false);
        }
    };

    return (
        <header className="shrink-0 bg-sidebar z-50 relative pt-[env(safe-area-inset-top,0px)]">
            <div className="flex h-14 items-center justify-between px-3 sm:px-4 md:px-6">
                {/* Left side - Logo (smaller on mobile) */}
                <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                    <img
                        src="/bls-logo.png"
                        alt={t('layout.header.blsLogo')}
                        className="h-8 w-auto sm:h-10 flex-shrink-0"
                    />
                </div>

                {/* Right side: language + user (touch-friendly on mobile) */}
                <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                    {/* Language Switcher */}
                    <LanguageSwitcher
                        showLabel={false}
                        className="bg-white/5 hover:bg-sidebar-accent border border-white/10 text-white [&_svg]:text-white hover:[&_svg]:text-white h-8 w-auto px-1.5 sm:h-9 sm:px-2 !py-0.5 justify-center gap-1 rounded-lg transition-all duration-200 [&_span]:text-xl min-h-[44px] sm:min-h-0"
                    />

                    {/* User Avatar Dropdown (same height as language switcher: 44px mobile, 36px sm+) */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 min-h-[44px] min-w-[44px] w-9 sm:min-h-0 sm:min-w-0 rounded-full p-0 hover:bg-sidebar-accent transition-all duration-200 touch-manipulation"
                            >
                                <Avatar className="size-11 sm:size-9 border border-sidebar-primary/20 transition-all duration-200 hover:scale-105 shadow-sm">
                                    <AvatarImage src="" alt={displayName} />
                                    <AvatarFallback className="bg-white/10 text-white text-[10px] sm:text-xs font-semibold backdrop-blur-sm">
                                        {userInitials}
                                    </AvatarFallback>
                                </Avatar>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            className="w-auto min-w-[15rem] max-w-xs rounded-xl border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.08)] bg-white p-2"
                            side="bottom"
                            align="end"
                            sideOffset={8}
                        >
                            <DropdownMenuLabel className="p-0 font-normal mb-1">
                                <div className="flex items-center gap-3 px-3 py-2.5 text-left text-sm bg-gray-50/50 rounded-lg mx-1">
                                    <Avatar className="h-9 w-9 border border-gray-200 shadow-sm shrink-0">
                                        <AvatarImage src="" alt={displayName} />
                                        <AvatarFallback className="bg-sidebar text-white text-xs font-semibold">
                                            {userInitials}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="grid flex-1 text-left text-sm leading-tight break-all">
                                        <span className="font-semibold text-gray-900">{displayName}</span>
                                        <span className="text-xs text-muted-foreground font-medium">{user?.email}</span>
                                    </div>
                                </div>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-gray-100 my-1" />
                            <DropdownMenuItem
                                onClick={handleLogoutClick}
                                className="focus:bg-red-50 focus:text-red-600 rounded-lg px-3 py-2.5 cursor-pointer font-medium text-gray-700 transition-colors my-0.5"
                            >
                                <LogOut className="me-3 h-4 w-4" />
                                {t('layout.header.logout')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Logout Confirmation Dialog */}
            <ConfirmationDialog
                isOpen={isLogoutDialogOpen}
                onClose={handleCancelLogout}
                onConfirm={handleConfirmLogout}
                title={t('dialogs.confirmLogout.title')}
                description={t('dialogs.confirmLogout.description')}
                confirmText={t('dialogs.confirmLogout.confirmText')}
                cancelText={t('dialogs.confirmLogout.cancelText')}
                isLoading={isLoggingOut}
            />
        </header>
    );
};
